import { CoreModel, MessageType, MessageMsgFrom } from '@tencent/dz-models';
import { logger, report, Request } from 'src/helper';
import { ImpConsultModel } from './ImpConsultModel';
import { ImpHeaderModel } from './ImpHeaderModel';
import { ImpMessageModel } from './ImpMessageModel';
import { ImpLeaveModel } from './ImpLeaveModel';
import { ImpMobileModel } from './ImpMobileModel';
import { ImpChatBotModel } from './ImpChatBotModel';
import { HeartBeatType, ChatActivate } from '../const';
import { jsonParseSandbox } from '../utils';

export class ImpCoreModel extends CoreModel {
  headerModel: ImpHeaderModel;
  consultModel: ImpConsultModel;
  messageModel: ImpMessageModel;
  leaveModel: ImpLeaveModel;
  mobileModel: ImpMobileModel;
  chatBotModel: ImpChatBotModel;
  request: Request;

  constructor() {
    super();
    const { citoken, consult } = (window as any).FMC_HOST;
    this.request = new Request({ citoken }, consult);
    this.consultModel = new ImpConsultModel(consult, citoken);
    this.headerModel = new ImpHeaderModel(this);
    this.messageModel = new ImpMessageModel(this);
    this.leaveModel = new ImpLeaveModel(this);
    this.mobileModel = new ImpMobileModel(this);
    this.chatBotModel = new ImpChatBotModel(this);
  }

  /**
   * dom load之前的逻辑
   */
  public beforeMounted() {
    logger.info('[ImpCoreModel]', 'consult page rum beforeMounted');

    // 1.手机号获取逻辑
    this.mobileModel.getPhoneNumberVerification();
    // 2. 非搜索的欢迎语在入口推送，搜索的在entry的时候推
    if (this.consultModel.isFeedConsult()) {
      this.leaveModel.fetchBusinessCard();
      this.leaveModel.pushGreeting();
      this.leaveModel.pushStaffContact();
    }

    // 审核场景下，默认出2个占位符
    if (this.consultModel.isAudit && this.consultModel.isFullSearch()) {
      this.messageModel.pushMessage({
        type: MessageType.Placeholder,
        msg_from: MessageMsgFrom.Server,
        payload: {},
      });
    }
  }

  /**
   * dom load之后，会话之前的一些业务
   * 下面的逻辑梳理，依据历史项目梳理的，可以根据业务模型重新划分，不一定遵循历史逻辑
   */
  public async mounted() {
    logger.info('[ImpCoreModel]', 'consult page rum mounted');

    if (this.consultModel.isAIStaffPreviewTraffic()) {
      await this.chatBotModel.getChatBotHistoryMsg();
      return;
    }

    this.beforeMounted();

    this.iframeMessageListerner();

    // 发送消息，获取性能指标
    this.consultModel.transferMessage(JSON.stringify({
      type: 'performance',
    }));

    // 2. 获取历史消息
    this.leaveModel.fetchHistoryMessage();

    // 消息更新监听
    this.messageUpdateListener();

    // 3. 推荐问题， 搜索不推荐config里面的
    if (!this.consultModel.isCursed && !this.messageModel.isRobot && this.consultModel.isFeedConsult()) {
      this.leaveModel.fetchRecommendation();
    }

    // 获取推荐内容
    if (this.consultModel.isFullSearch() && !this.consultModel.unConnectIm()) {
      // 获取搜索挽留问题
      this.leaveModel.fetchExitQuestions();

      // 获取蹊径搜索组件的推荐问题和活动卡片
      this.leaveModel.fetchSeachRecommendation().then((res: any) => {
        const { effectiveCardList, cardList, effectiveQuestionList, questionList } = res;
        // entry 卡片
        if (effectiveCardList) {
          this.messageModel.pushMessage(cardList);
        }
        // 欢迎语
        this.leaveModel.pushGreeting();
        this.leaveModel.pushStaffContact();
        // 搜索词预输入
        this.leaveModel.pushSearchTerm();
        // 问题推荐
        if (effectiveQuestionList) {
          this.messageModel.pushMessage(questionList);
        } else {
          // 关闭猜你想问定时器
          const timer = this.getHeaderModel()?.listenTimer;
          timer && clearInterval(timer);
        }
      });
    }

    // 4. 有一些连接会话的逻辑
    this.messageModel.connectRule();

    // 5. 挽留弹窗的一些信息获取，和监听
    // 搜索场景不从这里取
    if (!this.consultModel.isHalfConsult()) {
      // 挽留弹窗逻辑不是完整的
      this.leaveModel.listenerBrower();
    }

    window.addEventListener('beforeunload', () => {
      window.chatActivate = ChatActivate.un_active; // 会话激活状态 1-激活；2-未激活, 默认未激活
      report.heart({
        heart_beat_type: HeartBeatType.page_close,
        chat_activate: window.chatActivate,
      });
    });
  }

  public iframeMessageListerner = () => {
    // 一些window的事件监听逻辑
    // 这个逻辑触发不知道在哪
    window.addEventListener('message', (e) => {
      // 首先判断是否orgin 是蹊径域名
      const pattern = /h5.gdt.qq.com|xj.gdt.qq.com|test-xj.gdt.qq.com/i;
      if (!pattern.test(e.origin)) return;

      if (e.data === 'scrollToBottom') {
        // 半屏
        this.messageModel.autoScroll();
      } else {
        // 获取性能指标
        const { type, data = {} } = jsonParseSandbox(e.data);
        if (type === 'performance') {
          logger.info('[ImpCoreModel]', 'performance', data);
          this.reportPerformance(data);
        }
      }
    });
  };

  // 上报
  public reportPerformance(data: any) {
    const baseParam = {
      module_type: 'xj_page_initialization',
      module_chinese_type: '蹊径页面初始化',
      module_value: '',
    };
    report.exposure({
      ...baseParam,
      element_type: 'server',
      element_chinese_type: '后端服务渲染',
      timestamp: data.serverTimestamp || '',
    });
    report.exposure({
      ...baseParam,
      element_type: 'navigation_start',
      element_chinese_type: '准备加载页面',
      timestamp: data.navigationStart || '',
    });
    report.exposure({
      ...baseParam,
      element_type: 'iframe_init',
      element_chinese_type: '触发iframe',
      timestamp: data.iframeInitTime || '',
    });
  }

  public getConsultModel() {
    return this.consultModel;
  }

  public getHeaderModel() {
    return this.headerModel;
  }

  public getMessageModel() {
    return this.messageModel;
  }

  public getLeaveModel() {
    return this.leaveModel;
  }
  public getChatBotModel() {
    return this.chatBotModel;
  }

  private messageUpdateListener()  {
    this.messageModel.addListener('onMessageUpdate', () => {
      report.contentExposure.updateContentQueue(this.messageModel.getAllMessage());
    });
  };
}
